import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
// Free shipping bar live update
function updateFreeShippingBar(cartTotal) {
  const threshold = 80000; // R800 in cents
  const bar = document.getElementById('free-shipping-bar');
  if (!bar) return;

  const remaining = Math.max(0, threshold - cartTotal);
  const progress = Math.min(100, (cartTotal / threshold) * 100);
  const fill = bar.querySelector('.free-shipping-bar__fill');
  const message = bar.querySelector('.free-shipping-bar__message');

  if (fill) fill.style.width = progress + '%';

  if (message) {
    if (cartTotal >= threshold) {
      message.classList.add('free-shipping-bar__message--success');
      message.innerHTML = '🎉 You\'ve unlocked free shipping!';
    } else {
      message.classList.remove('free-shipping-bar__message--success');
      const remainingRand = Math.ceil(remaining / 100);
      message.innerHTML = `You're <strong>R${remainingRand}</strong> away from free shipping!`;
    }
  }
}

// Listen for cart updates (Tinker theme dispatches cart:updated or uses fetch)
document.addEventListener('cart:updated', (event) => {
  const cartTotal = event.detail?.cart?.total_price ?? 0;
  updateFreeShippingBar(cartTotal);
});

// Fallback: intercept Shopify cart API responses
const originalFetch = window.fetch;
window.fetch = async function(...args) {
  const response = await originalFetch.apply(this, args);
  const url = args[0]?.toString() ?? '';
  if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update')) {
    const clone = response.clone();
    clone.json().then(data => {
      const total = data?.total_price ?? data?.items_subtotal_price ?? null;
      if (total !== null) updateFreeShippingBar(total);
    }).catch(() => {});
  }
  return response;
};
